// Southward expansion of the approved Hachi study: three bounded buildings and flat alley detail.
// These painters intentionally do not change WORLD, collision tiles, POIs or simulation.
import type {HdBuilding,Rect} from '../../common/src/data';
import type {LightFn} from './hd-registry';
import {type G,K,HT,px,frame,bands,disc,dk,lt,mix,alpha,text,textW} from './hd-kit';
import {panel,label,fan,pipe,window} from './hd-hachi-study';
export const NEIGHBORHOOD_BUILDINGS=['ks-pachinko','ks-karaoke','ks-ramen'] as const;

function conduit(g:G,x:number,y:number,w:number){
 px(g,x,y,w,5,K.ink);px(g,x,y+1,w,2,K.metalHi);
 for(let k=8;k<w;k+=20)px(g,x+k,y-1,3,7,K.metalLo);
}
function shutters(g:G,x:number,y:number,w:number,h:number){
 panel(g,x,y,w,h,K.metalLo);for(let j=4;j<h-3;j+=4){px(g,x+3,y+j,w-6,1,K.metal);px(g,x+3,y+j+1,w-6,1,K.ink);}
 px(g,x+w/2-5,y+h-8,10,2,K.metalHi);
}
function lantern(g:G,x:number,y:number,c:string){
 px(g,x-1,y-6,2,6,K.metalLo);disc(g,x,y+7,7,10,dk(c,.15));
 for(const dx of [-4,0,4])px(g,x+dx,y,1,15,alpha(K.warmHi,.5));
 px(g,x-5,y-3,10,2,K.ink);px(g,x-5,y+16,10,2,K.ink);
}
function ventBank(g:G,x:number,y:number,n:number){for(let k=0;k<n;k++)fan(g,x+k*34,y);}

export function paintHachiNeighborhood(g:G,b:HdBuilding,x:number,y:number,L:LightFn){
 if(!(NEIGHBORHOOD_BUILDINGS as readonly string[]).includes(b.id))return;
 const w=b.w*HT,h=b.h*HT,rh=(b.h-b.face)*HT,fy=y+rh;
 const pachinko=b.id==='ks-pachinko',karaoke=b.id==='ks-karaoke';
 g.save();g.beginPath();g.rect(x,y,w,h);g.clip();
 // Roof maintenance edges tie the block together; interiors stay specific to each use.
 for(const yy of [y+12,fy-17]){px(g,x+12,yy,w-24,6,K.metalLo);for(let k=20;k<w-20;k+=20)px(g,x+k,yy,2,6,K.metalHi);}
 pipe(g,x+w-13,y+20,rh-44);
 if(pachinko){
  // Heavy extraction plant and framed illuminated rooftop sign, on the existing flat roof.
  panel(g,x+21,y+28,96,56,K.wallLo);ventBank(g,x+27,y+34,2);
  shutters(g,x+26,y+62,86,16);conduit(g,x+111,y+49,w-133);
  panel(g,x+35,y+100,w-72,55,K.metalLo);for(let k=0;k<5;k++)px(g,x+40,y+108+k*8,w-82,1,K.ink);
  label(g,'パチンコ',x+40,y+106,w-82,K.pink,3);
  for(const xx of [x+45,x+w-55]){px(g,xx,y+154,5,22,K.metal);px(g,xx-5,y+176,16,3,K.metalHi);}
  // Broad copper-toned pilasters and a marquee with warm lamps.
  bands(g,x+2,fy+2,w-4,h-rh-4,mix(K.wall,K.amber,.13),K.wallLo,7);
  for(const xx of [x+6,x+w-17]){px(g,xx,fy+5,11,87,K.woodHi);px(g,xx+3,fy+5,2,87,K.warmDim);}
  panel(g,x+21,fy+5,w-42,29,K.metalLo);text(g,'PACHINKO',x+(w-textW('PACHINKO',2))/2,fy+13,K.warmHi,2);
  for(let k=28;k<w-28;k+=12){px(g,x+k,fy+8,3,2,K.amber);px(g,x+k,fy+29,3,2,K.amber);}
  const door=x+w/2-22;window(g,door,fy+46,44,47,K.white);px(g,door+21,fy+49,2,42,K.metalLo);
  // Individual machines behind glass, not an undifferentiated bright texture.
  for(const left of [x+23,x+w/2+32]){window(g,left,fy+42,w/2-53,49,K.amber);for(let k=0;k<3;k++){const mx=left+6+k*20;panel(g,mx,fy+47,16,35,K.metalLo);disc(g,mx+8,fy+59,5,7,K.pink);disc(g,mx+8,fy+59,2,4,K.white);px(g,mx+3,fy+72,10,2,K.amber);}}
  L(x+25,fy+9,w-50,20,K.amber,.2,true);L(x+42,y+107,w-84,27,K.pink,.22,false,true);
 }else if(karaoke){
  // Acoustic service room and paired fan units; a skylight shows the private rooms below.
  panel(g,x+95,y+32,113,69,K.concreteLo);px(g,x+98,y+34,107,3,K.concreteHi);
  shutters(g,x+102,y+45,26,45);label(g,'AUDIO',x+142,y+49,54,K.white);
  ventBank(g,x+21,y+111,3);conduit(g,x+33,y+139,152);pipe(g,x+181,y+96,48);
  window(g,x+222,y+83,64,65,K.pink);for(let j=1;j<4;j++)px(g,x+225,y+84+j*15,58,2,K.metalLo);
  bands(g,x+2,fy+2,w-4,h-rh-4,mix(K.wall,K.pink,.15),K.wallLo,7);
  // Private-room windows and sound baffles above the entrance canopy.
  for(let k=0;k<5;k++){const wx=x+18+k*57;window(g,wx,fy+7,41,23,k%2?K.amber:K.pink);px(g,wx+3,fy+9,8,19,K.woodLo);px(g,wx+29,fy+9,8,19,K.woodLo);}
  label(g,'カラオケ',x+14,fy+35,w-28,K.pink,2);
  const door=x+w*.46;window(g,door,fy+62,43,31,K.pink);px(g,door+20,fy+65,2,25,K.metalLo);
  label(g,'ROOMS',x+29,fy+67,72,K.white);label(g,'OPEN',x+w-91,fy+67,67,K.pink);
  for(const xx of [x+7,x+w-11]){px(g,xx,fy+36,4,57,K.metal);px(g,xx+1,fy+39,1,50,K.pink);}
  // A narrow blade gives the building an identity from the adjoining lane.
  panel(g,x+w-36,y+147,27,83,K.redDk);frame(g,x+w-34,y+149,23,79,K.pink);
  for(let k=0;k<4;k++)text(g,'SING'[k],x+w-29,y+154+k*17,K.white,2);
  L(x+18,fy+41,w-36,18,K.pink,.24,true);
 }else{
  // Ramen: retain the tiled roof; add a kitchen exhaust and modest timber service shelter.
  panel(g,x+26,y+26,70,54,K.woodLo);bands(g,x+22,y+23,78,22,K.woodHi,K.woodLo,6);
  for(let k=0;k<7;k++)px(g,x+25+k*11,y+24,1,18,K.ink);
  panel(g,x+178,y+102,84,47,K.metalLo);shutters(g,x+183,y+108,74,34);
  conduit(g,x+117,y+124,64);pipe(g,x+112,y+55,75);disc(g,x+114,y+54,10,5,K.metalHi);
  // Raised fan collar stays inside the solid roof and has no animated smoke cost.
  fan(g,x+233,y+39);px(g,x+242,y+66,8,28,K.metalLo);
  bands(g,x+2,fy+2,w-4,h-rh-4,K.woodHi,K.woodLo,7);
  for(let k=9;k<w-8;k+=18)px(g,x+k,fy+4,2,89,K.woodLo);
  for(let k=0;k<4;k++){const wx=x+15+k*74;window(g,wx,fy+8,57,21,K.amber);for(let q=8;q<54;q+=9)px(g,wx+q,fy+10,1,17,K.woodLo);}
  // Deep sign fascia and noren curtains, with a lit kitchen visible on either side.
  label(g,'ラーメン',x+58,fy+34,w-116,K.amber,2);
  window(g,x+21,fy+61,95,29,K.amber);window(g,x+w-116,fy+61,95,29,K.amber);
  for(const xx of [x+28,x+w-109]){px(g,xx,fy+77,81,3,K.woodHi);for(let k=0;k<4;k++){disc(g,xx+8+k*20,fy+72,6,3,K.white);px(g,xx+4+k*20,fy+73,8,2,K.woodLo);}}
  panel(g,x+w/2-29,fy+60,58,34,K.woodLo);
  for(let k=0;k<4;k++){px(g,x+w/2-27+k*14,fy+59,12,19,K.redDk);px(g,x+w/2-26+k*14,fy+60,1,16,K.pink);}
  for(const xx of [x+17,x+w-18]){lantern(g,xx,fy+48,K.amber);L(xx-7,fy+46,14,20,K.amber,.3,true);}
  L(x+61,fy+40,w-122,15,K.amber,.25,true);
 }
 // One continuous facade foot: depth without extending onto the alley.
 px(g,x+2,y+h-4,w-4,3,K.ink);px(g,x+5,y+h-4,w-10,1,K.metalLo);
 g.restore();
}

/** Low-contrast flat details along the two existing alleys and the southern sidewalk. */
export function paintNeighborhoodGround(g:G,R:Rect){
 const x0=Math.max(46,R.x),x1=Math.min(74,R.x+R.w),y0=Math.max(70,R.y),y1=Math.min(84,R.y+R.h);
 if(x1<=x0||y1<=y0)return;
 const X=(x:number)=>(x-R.x)*HT,Y=(y:number)=>(y-R.y)*HT;
 g.save();g.beginPath();g.rect(X(x0),Y(y0),(x1-x0)*HT,(y1-y0)*HT);g.clip();
 for(const ty of [70,82]){
  // Longitudinal gutter sits beside the wall, leaving the center of each lane visually clear.
  px(g,X(46),Y(ty)+5,28*HT,3,alpha(K.ink,.3));
  for(let tx=47;tx<73;tx+=5){panel(g,X(tx)+8,Y(ty)+8,25,7,K.metalLo);for(let k=3;k<23;k+=4)px(g,X(tx)+8+k,Y(ty)+9,1,5,K.ink);}
  // Utility covers, worn repairs and tiny pieces of paper painted flat on the ground.
  for(const tx of [48,59,70]){frame(g,X(tx)+6,Y(ty)+35,26,18,alpha(K.metalHi,.27));px(g,X(tx)+9,Y(ty)+39,20,1,alpha(K.ink,.5));px(g,X(tx)+15,Y(ty)+47,6,2,K.seam);}
  for(const [tx,c]of [[50,K.amber],[59,K.pink],[68,K.amber]] as const){for(let j=0;j<10;j++)px(g,X(tx)-23+j%3,Y(ty)+10+j*3,46-j*3,1,alpha(c,.08));}
 }
 g.restore();
}
